/**
 * File: invoiceLedger.ts
 * Path: packages/config/src/invoices/invoiceLedger.ts
 * Description: Every invoice the company has issued, read from the books.
 *
 * The books are the one record. An invoice is the journal that billed it
 * (Dr Trade debtors, Cr Sales); its payments, credit notes and corrections
 * are the Trade debtors lines tagged with its number. So an invoice's
 * balance is the sum of its tagged lines, and the list's unpaid total is the
 * Trade debtors account itself:
 *
 *   Trade debtors = open invoices + overpaid invoices + lines not tied to an invoice
 *
 * holds by construction, and the page shows the last part when it isn't nil
 * so nothing is hidden. Saved invoice documents (invoices/<id>) add only
 * what the books don't know: drafts, whether it has been sent, a void.
 *
 * Journals brought in from Zoho before lines carried tags are read the way
 * Zoho booked them: "zoho:invoice:<number>" bills that number, and a Zoho
 * payment pays the invoices its narration names, oldest first.
 *
 * Pure: the admin and the server both call it, so they show one figure.
 */

import type { Journal, LedgerAccount } from "../accounting/accountingTypes";
import { normaliseName } from "../contracts/contractImportRows";
import { parseInvoiceNumber, type SavedInvoice } from "./invoiceRecord";

/** With no due date of its own, an invoice falls due this many days after its date. */
export const LEDGER_DUE_DAYS = 30;

export const INVOICE_STATUSES = ["draft", "unbooked", "notSent", "sent", "partPaid", "overdue", "paid", "credited", "void"] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: "Draft",
  unbooked: "Not in the books",
  notSent: "Not sent",
  sent: "Sent",
  partPaid: "Part paid",
  overdue: "Overdue",
  paid: "Paid",
  credited: "Credited",
  void: "Void",
};

/** The journal reference an invoice issued in the admin is booked under. */
export const invoiceJournalRef = (number: string) => `invoice:${number}`;
/** A credit note's journal reference. */
export const creditNoteJournalRef = (number: string) => `creditnote:${number}`;

export type MovementKind = "issued" | "payment" | "creditNote" | "creditApplied" | "other";

/** One thing that changed what an invoice owes. `cents` is signed: positive raises the balance. */
export interface InvoiceMovement {
  journalId: string;
  journalNumber: number;
  date: string;
  kind: MovementKind;
  /** True on a reversal journal: it undoes an earlier movement of the same kind. */
  reversal: boolean;
  cents: number;
  narration: string;
  /** Read from Zoho's narration, not a tag on the line: shown so Liam can check it. */
  inferred: boolean;
}

export interface LedgerInvoice {
  /** "INV-000012"; "" on a draft. */
  number: string;
  source: "zoho" | "admin";
  /** invoices/<id> for one made in the admin. */
  invoiceId: string | null;
  /** The journal that billed it. */
  journalId: string | null;
  journalNumber: number | null;
  issuedOn: string;
  dueOn: string;
  party: string;
  clientId: string | null;
  contractId: string | null;
  /** What the client pays with; for Zoho's invoices, the contract payment it was linked to, if any. */
  paymentReference: string;
  retainerId: string | null;
  totalCents: number;
  paidCents: number;
  creditedCents: number;
  /** total - paid - credited. Below nil when the client paid too much. */
  balanceCents: number;
  status: InvoiceStatus;
  /** Days past the due date while something is still owed; 0 otherwise. */
  daysLate: number;
  sent: boolean;
  movements: InvoiceMovement[];
}

/** A Trade debtors line no invoice claims: a manual journal, an opening balance or money Zoho left unused. */
export interface UnallocatedItem {
  journalId: string;
  journalNumber: number;
  date: string;
  party: string;
  clientId: string | null;
  /** Signed: positive is owed by the client, negative is the client's money not yet used. */
  cents: number;
  narration: string;
}

/** Money a client paid before an invoice existed (the Client credit account), per client. */
export interface ClientCredit {
  party: string;
  clientId: string | null;
  /** What the business still holds for them. */
  cents: number;
  /** The payment references it was paid against, when known. */
  references: string[];
}

export interface InvoiceLedger {
  asAt: string;
  /** Newest first; drafts first of all. */
  invoices: LedgerInvoice[];
  unallocated: UnallocatedItem[];
  clientCredits: ClientCredit[];
  /** The Trade debtors balance at asAt. */
  controlCents: number;
  /** Everything still owed on invoices. */
  openCents: number;
  /** Invoices paid more than their balance (a negative amount). */
  overpaidCents: number;
  /** Trade debtors lines no invoice claims. */
  unallocatedCents: number;
  /** The Client credit account: money held for clients, not income. */
  clientCreditCents: number;
}

/** A link from a brought-in contract's payment to a Zoho invoice (contracts/<id>/invoiceLinks). */
export interface LedgerLink {
  contractId: string;
  reference: string;
  number: string;
}

export interface LedgerInput {
  journals: readonly Journal[];
  accounts: readonly LedgerAccount[];
  saved?: readonly SavedInvoice[];
  links?: readonly LedgerLink[];
  /** Due dates known from elsewhere (a linked contract payment), by invoice number. */
  dueOn?: ReadonlyMap<string, string>;
  /** Only journals on or before this day count; also the day lateness is measured to. */
  asAt: string;
}

const DAY_MS = 86_400_000;
const dayNumber = (iso: string) => Date.parse(`${iso}T00:00:00Z`) / DAY_MS;
export const addDays = (iso: string, days: number): string =>
  new Date(Date.parse(`${iso}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);

const INVOICE_IN_TEXT = /INV-\d{1,9}/g;

/** Invoice numbers a narration names, in the order given. */
export const invoiceNumbersIn = (text: string): string[] => [...new Set(text.toUpperCase().match(INVOICE_IN_TEXT) ?? [])];

/** The invoice number a journal bills, when it is an invoice's own journal. */
export const billedNumber = (journal: Pick<Journal, "externalRef">): string | null => {
  const ref = journal.externalRef ?? "";
  if (!ref.startsWith("zoho:invoice:") && !ref.startsWith("invoice:")) return null;
  const n = parseInvoiceNumber(ref);
  return n === null ? null : `INV-${String(n).padStart(6, "0")}`;
};

const directKind = (journal: Journal, creditIds: ReadonlySet<string>): MovementKind => {
  const ref = journal.externalRef ?? "";
  if (billedNumber(journal)) return "issued";
  if (ref.startsWith("creditnote:")) return "creditNote";
  if (journal.lines.some((l) => creditIds.has(l.accountId))) return "creditApplied";
  if (ref.startsWith("zoho:payment:") || ref.startsWith("crm:payment:") || ref.startsWith("receipt:") || ref.startsWith("bank:") || ref.startsWith("reclass:")) {
    return "payment";
  }
  return "other";
};

const partyKey = (clientId: string | null, party: string) => (clientId ? `id:${clientId}` : `name:${normaliseName(party)}`);

interface Draft {
  number: string;
  issue: Journal | null;
  totalCents: number;
  movements: InvoiceMovement[];
}

export const buildInvoiceLedger = (input: LedgerInput): InvoiceLedger => {
  const { asAt } = input;
  const debtorIds = new Set(input.accounts.filter((a) => a.role === "debtors").map((a) => a.id));
  const creditIds = new Set(input.accounts.filter((a) => a.role === "clientCredit").map((a) => a.id));
  const journals = input.journals
    .filter((j) => j.date <= asAt)
    .sort((a, b) => a.date.localeCompare(b.date) || a.number - b.number);
  const byId = new Map(input.journals.map((j) => [j.id, j]));

  const kindOf = (journal: Journal): MovementKind => {
    if (journal.source === "reversal" && journal.reversalOf) {
      const original = byId.get(journal.reversalOf);
      return original ? directKind(original, creditIds) : "other";
    }
    return directKind(journal, creditIds);
  };

  const invoices = new Map<string, Draft>();
  const get = (number: string): Draft => {
    let d = invoices.get(number);
    if (!d) {
      d = { number, issue: null, totalCents: 0, movements: [] };
      invoices.set(number, d);
    }
    return d;
  };
  const movement = (journal: Journal, cents: number, inferred: boolean): InvoiceMovement => ({
    journalId: journal.id,
    journalNumber: journal.number,
    date: journal.date,
    kind: kindOf(journal),
    reversal: journal.source === "reversal",
    cents,
    narration: journal.narration,
    inferred,
  });

  // Pass 1: the journal that billed each invoice, so later payments know what they are paying.
  for (const journal of journals) {
    if (journal.source === "reversal") continue;
    const billed = billedNumber(journal);
    if (!billed) continue;
    const debit = journal.lines
      .filter((l) => debtorIds.has(l.accountId) && (l.invoice ?? billed) === billed)
      .reduce((s, l) => s + l.debitCents - l.creditCents, 0);
    if (debit <= 0) continue;
    const d = get(billed);
    if (d.issue) continue;
    d.issue = journal;
    d.totalCents = debit;
  }

  const unallocated: UnallocatedItem[] = [];
  const credits = new Map<string, ClientCredit>();
  let controlCents = 0;
  let clientCreditCents = 0;

  // What a Zoho payment's narration was read as paying, so its reversal can undo exactly that.
  const inferred = new Map<string, { number: string; cents: number }[]>();
  const roomOn = (number: string) => {
    const d = invoices.get(number);
    if (!d?.issue) return 0;
    return d.totalCents + d.movements.filter((m) => m.kind !== "issued").reduce((s, m) => s + m.cents, 0);
  };
  const leaveUnallocated = (journal: Journal, cents: number) =>
    unallocated.push({
      journalId: journal.id,
      journalNumber: journal.number,
      date: journal.date,
      party: journal.party ?? "No name given",
      clientId: journal.clientId,
      cents,
      narration: journal.narration,
    });

  // Pass 2: every Trade debtors and Client credit line, in date order.
  for (const journal of journals) {
    const original = journal.source === "reversal" && journal.reversalOf ? (byId.get(journal.reversalOf) ?? null) : null;
    const billed = billedNumber(original ?? journal);
    for (const line of journal.lines) {
      if (creditIds.has(line.accountId)) {
        const held = line.creditCents - line.debitCents;
        clientCreditCents += held;
        const key = partyKey(journal.clientId, journal.party ?? "");
        const entry = credits.get(key) ?? { party: journal.party ?? "No name given", clientId: journal.clientId, cents: 0, references: [] };
        entry.cents += held;
        if (line.invoice && !entry.references.includes(line.invoice)) entry.references.push(line.invoice);
        credits.set(key, entry);
        continue;
      }
      if (!debtorIds.has(line.accountId)) continue;
      const cents = line.debitCents - line.creditCents;
      controlCents += cents;
      const tag = line.invoice ?? billed;
      if (tag) {
        get(tag).movements.push(movement(journal, cents, false));
        continue;
      }
      // The reversal of a Zoho payment read from its narration undoes the same split.
      const undo = original ? inferred.get(original.id) : undefined;
      if (undo) {
        undo.forEach((a) => get(a.number).movements.push(movement(journal, -a.cents, true)));
        const rest = cents + undo.reduce((s, a) => s + a.cents, 0);
        if (rest !== 0) leaveUnallocated(journal, rest);
        continue;
      }
      // A Zoho payment from before lines were tagged: the invoices its narration names, oldest first.
      const named = (journal.externalRef ?? "").startsWith("zoho:payment:") && cents < 0 ? invoiceNumbersIn(journal.narration) : [];
      const split: { number: string; cents: number }[] = [];
      let left = -cents;
      named
        .filter((n) => invoices.get(n)?.issue)
        .sort((a, b) => invoices.get(a)!.issue!.date.localeCompare(invoices.get(b)!.issue!.date) || a.localeCompare(b))
        .forEach((n) => {
          const take = Math.min(left, Math.max(0, roomOn(n)));
          if (take <= 0) return;
          get(n).movements.push(movement(journal, -take, true));
          split.push({ number: n, cents: -take });
          left -= take;
        });
      if (split.length > 0) inferred.set(journal.id, split);
      const rest = split.length > 0 ? -left : cents;
      if (rest !== 0) leaveUnallocated(journal, rest);
    }
  }

  const savedByNumber = new Map((input.saved ?? []).filter((s) => s.number).map((s) => [s.number, s]));
  const linkByNumber = new Map((input.links ?? []).map((l) => [l.number, l]));

  const rows: LedgerInvoice[] = [...invoices.values()].map((d) => {
    const saved = savedByNumber.get(d.number) ?? null;
    const link = linkByNumber.get(d.number) ?? null;
    const issue = d.issue;
    const issuedOn = issue?.date ?? saved?.draft.issuedOn ?? "";
    const dueOn =
      issue?.dueOn ?? (saved?.draft.dueOn || null) ?? input.dueOn?.get(d.number) ?? (issuedOn ? addDays(issuedOn, LEDGER_DUE_DAYS) : "");
    const sum = (kinds: MovementKind[]) => d.movements.filter((m) => kinds.includes(m.kind)).reduce((s, m) => s - m.cents, 0);
    const paidCents = sum(["payment", "creditApplied", "other"]);
    const creditedCents = sum(["creditNote"]);
    const balanceCents = d.movements.reduce((s, m) => s + m.cents, 0);
    const totalCents = issue ? d.totalCents : 0;
    const sent = issue?.externalRef?.startsWith("zoho:") ? true : saved?.sentAt != null;
    const voided = saved?.status === "void" || Boolean(issue?.reversedBy);
    const daysLate = balanceCents > 0 && dueOn ? Math.max(0, Math.round(dayNumber(asAt) - dayNumber(dueOn))) : 0;
    const status: InvoiceStatus = voided
      ? "void"
      : !issue
        ? "unbooked"
        : balanceCents <= 0
          ? creditedCents >= totalCents && totalCents > 0
            ? "credited"
            : "paid"
          : daysLate > 0
            ? "overdue"
            : paidCents + creditedCents > 0
              ? "partPaid"
              : sent
                ? "sent"
                : "notSent";
    return {
      number: d.number,
      source: issue?.externalRef?.startsWith("zoho:") ? "zoho" : "admin",
      invoiceId: saved?.id ?? null,
      journalId: issue?.id ?? null,
      journalNumber: issue?.number ?? null,
      issuedOn,
      dueOn,
      party: issue?.party ?? saved?.draft.client.name ?? "",
      clientId: issue?.clientId ?? saved?.clientId ?? null,
      contractId: saved?.contractId ?? link?.contractId ?? null,
      paymentReference: saved?.draft.paymentReference || link?.reference || "",
      retainerId: saved?.retainerId ?? null,
      totalCents,
      paidCents,
      creditedCents,
      balanceCents,
      status,
      daysLate,
      sent,
      movements: d.movements,
    };
  });

  // Saved in the admin but never booked: drafts, and invoices numbered before invoices went into the books.
  for (const saved of input.saved ?? []) {
    if (saved.status === "discarded") continue;
    if (saved.number && invoices.has(saved.number)) continue;
    const total = saved.draft.lines.reduce((s, l) => s + (l.cents > 0 ? l.cents : 0), 0);
    rows.push({
      number: saved.number,
      source: "admin",
      invoiceId: saved.id,
      journalId: null,
      journalNumber: null,
      issuedOn: saved.draft.issuedOn,
      dueOn: saved.draft.dueOn,
      party: saved.draft.client.name,
      clientId: saved.clientId,
      contractId: saved.contractId,
      paymentReference: saved.draft.paymentReference,
      retainerId: saved.retainerId,
      totalCents: total,
      paidCents: 0,
      creditedCents: 0,
      balanceCents: 0,
      status: saved.status === "draft" ? "draft" : saved.status === "void" ? "void" : "unbooked",
      daysLate: 0,
      sent: saved.sentAt !== null,
      movements: [],
    });
  }

  rows.sort((a, b) => {
    const draftA = a.status === "draft" ? 0 : 1;
    const draftB = b.status === "draft" ? 0 : 1;
    return draftA - draftB || b.issuedOn.localeCompare(a.issuedOn) || b.number.localeCompare(a.number);
  });

  const booked = rows.filter((r) => r.journalId !== null);
  const openCents = booked.reduce((s, r) => s + Math.max(0, r.balanceCents), 0);
  const overpaidCents = booked.reduce((s, r) => s + Math.min(0, r.balanceCents), 0);
  const unallocatedCents = controlCents - openCents - overpaidCents;
  return {
    asAt,
    invoices: rows,
    unallocated,
    clientCredits: [...credits.values()].filter((c) => c.cents !== 0).sort((a, b) => b.cents - a.cents),
    controlCents,
    openCents,
    overpaidCents,
    unallocatedCents,
    clientCreditCents,
  };
};

/** The invoices that still have something to pay, oldest first: what a payment is matched against. */
export const openInvoices = (ledger: Pick<InvoiceLedger, "invoices">): LedgerInvoice[] =>
  ledger.invoices
    .filter((r) => r.journalId !== null && r.status !== "void" && r.balanceCents > 0)
    .sort((a, b) => a.issuedOn.localeCompare(b.issuedOn) || a.number.localeCompare(b.number));

/** The oldest unpaid invoice's date, or null when nothing is owed. */
export const oldestUnpaidOn = (ledger: Pick<InvoiceLedger, "invoices">): string | null => openInvoices(ledger)[0]?.issuedOn ?? null;

/** True when the name and client point at the same customer. */
export const sameCustomer = (
  a: { clientId: string | null; party: string },
  b: { clientId: string | null; party: string },
): boolean =>
  a.clientId !== null && b.clientId !== null ? a.clientId === b.clientId : normaliseName(a.party) !== "" && normaliseName(a.party) === normaliseName(b.party);
